import { fetchMFAPreference, setUpTOTP, updateMFAPreference, verifyTOTPSetup } from "aws-amplify/auth";
import QRCode from "qrcode";
import { useEffect, useState, type FormEvent } from "react";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, type Me } from "../lib/api";
import { useSession } from "../lib/session";
import { PRODUCT_NAME } from "../lib/config";

type State = { kind: "loading" } | { kind: "off" } | { kind: "on" } | { kind: "setup"; qr: string; secret: string };

/** Optional two-step login with an authenticator app (TOTP), per the design doc. */
export function Security({ me }: { me: Me }) {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [code, setCode] = useState("");
  const { pending, error, run } = useAction();

  useEffect(() => {
    fetchMFAPreference()
      .then((p) => setState({ kind: p.enabled?.includes("TOTP") ? "on" : "off" }))
      .catch(() => setState({ kind: "off" }));
  }, []);

  const start = () =>
    void run(async () => {
      const details = await setUpTOTP();
      const uri = details.getSetupUri(PRODUCT_NAME, me.user.email);
      const qr = await QRCode.toDataURL(uri.toString(), { margin: 0, width: 352, color: { dark: "#161513", light: "#ffffff" } });
      setState({ kind: "setup", qr, secret: details.sharedSecret });
    });

  const verify = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await verifyTOTPSetup({ code: code.trim() });
      await updateMFAPreference({ totp: "PREFERRED" });
      setCode("");
      setState({ kind: "on" });
    });
  };

  const turnOff = () =>
    void run(async () => {
      await updateMFAPreference({ totp: "DISABLED" });
      setState({ kind: "off" });
    });

  return (
    <>
      <div className="page-head">
        <p className="label">Your account</p>
        <h1 className="display">
          Make it <em>yours</em>.
        </h1>
        <p className="lede">
          The name your colleagues see, and two-step login: you hold candidates' CVs and your company's finances, so a
          stolen password shouldn't be enough.
        </p>
      </div>

      <DisplayName me={me} />

      <section className="section" aria-labelledby="mfa-h">
        <div className="section-head">
          <h2 id="mfa-h" className="label">
            Authenticator app
          </h2>
          <span className={`status ${state.kind === "on" ? "" : "off"}`}>{state.kind === "on" ? "On" : "Off"}</span>
        </div>

        <div className="form narrow">
          <ErrorNotice error={error} />

          {state.kind === "off" && (
            <>
              <p>Use any authenticator app, such as Google Authenticator, 1Password or Microsoft Authenticator.</p>
              <div>
                <button className="btn" onClick={start} disabled={pending}>
                  {pending ? "Preparing…" : "Turn on"}
                </button>
              </div>
            </>
          )}

          {state.kind === "setup" && (
            <form className="form" onSubmit={verify}>
              <p>
                <span className="figures">01</span> Scan this with your authenticator app.
              </p>
              <img className="qr" src={state.qr} alt="QR code for your authenticator app" />
              <p className="hint">
                Can't scan? Enter this key: <code className="figures">{state.secret}</code>
              </p>
              <p>
                <span className="figures">02</span> Enter the 6-digit code it shows.
              </p>
              <Field
                label="Code"
                className="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <div>
                <button className="btn" disabled={pending}>
                  {pending ? "Checking…" : "Confirm"}
                </button>
              </div>
            </form>
          )}

          {state.kind === "on" && (
            <>
              <p>You'll be asked for a code from your authenticator app each time you log in.</p>
              <div>
                <button className="btn secondary" onClick={turnOff} disabled={pending}>
                  Turn off
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

/** The name colleagues see on jobs, history and the team table. */
function DisplayName({ me }: { me: Me }) {
  const { refresh } = useSession();
  const [name, setName] = useState(me.user.displayName ?? "");
  const [saved, setSaved] = useState(false);
  const { pending, error, run } = useAction();
  const unchanged = name.trim() === (me.user.displayName ?? "");

  const save = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.updateMe(name.trim());
      await refresh();
      setSaved(true);
    });
  };

  return (
    <section className="section" aria-labelledby="name-h">
      <div className="section-head">
        <h2 id="name-h" className="label">
          Your name
        </h2>
        <span className="label figures">{me.user.email}</span>
      </div>
      <form className="form narrow" onSubmit={save}>
        <Field
          label="Name"
          hint="What your colleagues see instead of your email. Leave blank to show your email."
          autoComplete="name"
          maxLength={80}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setSaved(false);
          }}
          placeholder="e.g. Sam Smith"
        />
        <ErrorNotice error={error} />
        <div>
          <button className="btn secondary" disabled={pending || unchanged}>
            {pending ? "Saving…" : saved && unchanged ? "Saved" : "Save name"}
          </button>
        </div>
      </form>
    </section>
  );
}
